import React, { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Config } from "@/config/Config";
import { Badge } from "@/components/common/Badge";
import { Shield, User, KeyRound, LogOut, Menu } from "lucide-react";
import { ChangePasswordModal } from "@/features/auth/ChangePasswordModal";

interface NavbarProps {
  onToggleSidebar?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onToggleSidebar }) => {
  const { user, logout } = useAuth();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  return (
    <>
      <header className="navbar">
        <div className="navbar-left">
          {onToggleSidebar && (
            <button
              type="button"
              className="btn-icon mobile-only"
              onClick={onToggleSidebar}
              aria-label="Abrir menu lateral"
            >
              <Menu size={20} />
            </button>
          )}
          <div className="navbar-brand">
            <div className="brand-icon">
              <Shield size={22} className="text-primary" />
            </div>
            <div>
              <span className="brand-title">{Config.appTitle}</span>
              <span className="brand-subtitle">IAM Multi-Tenant</span>
            </div>
          </div>
        </div>

        <div className="navbar-right">
          {user && (
            <div className="user-profile-widget">
              <button
                type="button"
                className="user-badge-trigger"
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsUserMenuOpen((prev) => !prev);
                  }
                }}
                aria-haspopup="true"
                aria-expanded={isUserMenuOpen}
              >
                <div className="avatar">
                  <User size={16} />
                </div>
                <div className="user-details hide-sm">
                  <span className="user-name">{user.name}</span>
                  <div className="user-roles">
                    {user.roles.slice(0, 2).map((role) => (
                      <Badge key={role} variant="info" className="text-xs py-0">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              </button>

              {isUserMenuOpen && (
                <>
                  <button
                    type="button"
                    className="dropdown-overlay"
                    onClick={() => setIsUserMenuOpen(false)}
                    aria-label="Fechar menu de usuário"
                    tabIndex={-1}
                  />
                  <div className="dropdown-menu animate-fade-in" role="menu">
                    <div className="dropdown-header">
                      <p className="font-semibold text-sm">{user.name}</p>
                      <p className="text-xs text-muted truncate">{user.email}</p>
                      <div className="mt-2 text-xs text-muted">
                        Tenant ID:{" "}
                        <code className="text-primary font-mono">
                          {user.application_id || "auth-admin"}
                        </code>
                      </div>
                    </div>
                    <div className="dropdown-divider" />
                    <button
                      type="button"
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setIsPasswordModalOpen(true);
                      }}
                    >
                      <KeyRound size={16} />
                      <span>Alterar Minha Senha</span>
                    </button>
                    <button
                      type="button"
                      className="dropdown-item text-red-600 hover:bg-red-50"
                      role="menuitem"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        void logout();
                      }}
                    >
                      <LogOut size={16} />
                      <span>Sair do Portal</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </>
  );
};
